import { defineStore } from "pinia";
import { showError } from "src/utils/notify";
import { Buffer } from "buffer";
import { useUserInfo } from "./user-store";
import {
  KlaviyoEventTypes,
  useklaviyoEventsStores,
} from "./klaviyo-events-store";
import { useCommonStore } from "./common-store";
import { getAccountID } from "src/utils/commonFunction";
import { withAllowedCoordinates } from "src/utils/geolocation";

// Missing ids can reach us as text once they pass through a page URL.
const isValidStoreId = (id) => !!id && id !== "undefined" && id !== "null";

export const useStoreDetails = defineStore("mystores", {
  state: () => ({
    store: {},
    loadStores: false,
    offers: {},
    teamLevel: [],
    loadTeamLevel: false,
    teams: [],
    loadTeam: false,
    deals: [],
    loadDeals: false,
    findStore: [],
    loadFindStore: false,
    friendTeamLevel: [],
    friendloadTeamLevel: false,

    friendTeamList: [],
    loadFriendTeams: false,
    earningHistory: [],
    loadEarningHistory: false,

    tagList: [],
    loadTagList: false,
    distanceList: {},
    loadDistanceList: false,

    couponsList: [],
    loadCoupons: false,

    reviews: [],
    loadReviews: false,

    reviewDetail: [],
    loadReviewDetail: false,

    teamByLevel: [],
    loadTeamByLevel: false,

    loyaltyOffers: {},

    rjrDeals: {},

    dealsGroup: [],

    tierInfo: null,
  }),
  getters: {
    getStoreOfferCouponsList(state) {
      return state.offers;
    },
  },
  actions: {
    async getStoresDetails(id) {
      if (!isValidStoreId(id)) return;
      try {
        // this.store = {};
        this.offers = {};
        this.loadStores = false;
        let data = await this.$request.get(
          `${this.apiEndpoints.STORE_DETAILS(id)}?acc=${id}`
        );
        if (data.data) {
          this.store = data.data.item;
          this.loadStores = true;
          //the white-label store's own details carry the phone verification
          //switches, reuse this response instead of fetching them again
          if (id && id == getAccountID())
            useUserInfo().setPhoneLinkVerificationSettings(data.data.item);
          this.getStoreTierInfo(id);
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    // Tier is tracked per store, only for customers registered with a store that has active tiers.
    async getStoreTierInfo(id) {
      if (!this.store?.tier_exists_for_account || !this.store?.registered) {
        this.tierInfo = null;
        return;
      }
      try {
        const data = await this.$request.get(
          this.apiEndpoints.STORE_TIER_INFO(id)
        );
        this.tierInfo = data?.data?.item || null;
      } catch (error) {
        this.tierInfo = null;
      }
    },
    async getStoreOffers(storeId) {
      if (!isValidStoreId(storeId)) return;
      try {
        let data = await this.$request.get(
          this.apiEndpoints.STORE_OFFER_COUPONS(storeId)
        );
        if (data.status) {
          this.offers.links = data.data.links;
        }
      } catch (error) {
        showError(error.message);
      }
    },
    async getStoreThirdPartyOffers(storeId) {
      if (!isValidStoreId(storeId)) return;
      try {
        let data = await this.$request.get(
          `${this.apiEndpoints.STORE_OFFER_THIRDPARTY_COUPONS(
            storeId
          )}?acc=${storeId}`
        );
        if (data.status) {
          this.offers.thirdPartyCoupons = data.data.items;
        }
      } catch (error) {
        showError(error.message);
      }
    },
    async getInAppPromotionsOffers(storeId) {
      if (!isValidStoreId(storeId)) return;
      try {
        let data = await this.$request.get(
          this.apiEndpoints.STORE_OFFER_IN_APP_PROMOTION(storeId)
        );
        if (data.status) {
          return data;
        }
      } catch (error) {
        showError(error.message);
      }
    },
    async getMyTeamLevel(id) {
      try {
        this.teamLevel = [];
        this.loadTeamLevel = false;
        let data = await this.$request.get(
          `${this.apiEndpoints.TEAM_LEVEL(id)}?acc=${id}`
        );
        if (data.data) {
          this.teamLevel = data.data.items;
          this.loadTeamLevel = true;
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    async getAllTeamLevel(id) {
      try {
        this.teams = [];
        this.loadTeam = false;
        let data = await this.$request.get(this.apiEndpoints.TEAM_ALL(id));
        if (data.data) {
          this.teams = data.data.items;
          this.loadTeam = true;
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    resetTeamByLevel() {
      this.teamByLevel = [];
      this.loadTeamByLevel = false;
    },
    async getTeamByLevel(id, teamlevel, params) {
      try {
        let data = await this.$request.get(
          this.apiEndpoints.TEAM_ALL(id) +
            "?level_number=" +
            teamlevel +
            "&page=" +
            params.page +
            "&per_page=" +
            params.per_page +
            "&acc=" +
            id
        );
        if (params.page == 1) {
          this.teamByLevel = [];
        }
        if (data.data && data.data.items && data.data.items.length > 0) {
          Array.prototype.push.apply(this.teamByLevel, data.data.items);
          this.loadTeamByLevel = true;
        }
        return data.data;
      } catch (error) {
        return null;
        // console.log("FromAPI:-" + error);
      }
    },
    async getStoreDeals(storeid, param = {}) {
      try {
        param.acc = storeid;
        let response = await this.$request.get(
          this.apiEndpoints.STORE_DEALS(storeid, param)
        );
        if (response.data) {
          this.loadDeals = true;
          if (param.hasOwnProperty("page") && param.page == 1) {
            this.deals = [];
            this.deals = response?.data?.items ? response?.data?.items : [];
          } else {
            Array.prototype.push.apply(
              this.deals,
              response?.data?.items ? response?.data?.items : []
            );
          }
        }
        return response.data;
      } catch (error) {
        // console.log("APIError:-", error);
      }
    },
    async addFeedback(feedback, id) {
      try {
        feedback.acc = id;
        let data = await this.$request.post(
          this.apiEndpoints.ADD_FEEDBACK(id),
          feedback
        );
        if (data.data && data.data.status) {
          const klaviyoStore = useklaviyoEventsStores();
          klaviyoStore.addKlaviyoEvent({
            event_name: KlaviyoEventTypes.STORE_FEEDBACK,
            store_id: id,
            rating: feedback.rating,
            notes: feedback.notes,
          });

          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {}
    },
    resetFindStoreData() {
      this.findStore = [];
      this.loadFindStore = false;
    },
    async findStores(param) {
      try {
        let data = await this.$request.get(
          this.apiEndpoints.FIND_STORES + new URLSearchParams(param).toString()
        );
        if (data.data) {
          if (param.page == 1) {
            this.resetFindStoreData();
          }
          Array.prototype.push.apply(
            this.findStore,
            data.data.items ? data.data.items : []
          );
          this.loadFindStore = true;
        }

        return data.data;
      } catch (error) {
        if (error.hasOwnProperty("code") && error.code == "ECONNABORTED") {
          showError("Please try after sometime");
          this.loadFindStore = true;
          return { errorCode: "NO_ITEMS" };
        } else if (
          error.response.hasOwnProperty("status") &&
          error.response.status == 403
        ) {
          showError("Please try after sometime");
          this.loadFindStore = true;
          return { errorCode: "NO_ITEMS" };
        }
      }
    },
    async joinStore(id) {
      try {
        let data = await this.$request.post(this.apiEndpoints.JOIN_STORE(id), {
          enforce_age_restrictions: 1,
          acc: id,
        });
        useCommonStore().getAlMyStores();
        return data.data;
      } catch (error) {
        return error;
      }
    },
    async getStoreInterests(id) {
      try {
        let data = await this.$request.get(
          this.apiEndpoints.STORE_INTERESTS(id)
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async updateStorePrefs(id, params) {
      try {
        params.acc = id;
        let data = await this.$request.post(
          this.apiEndpoints.STORE_PREFS(id),
          params
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async removeStore(id) {
      try {
        let data = await this.$request.get(
          `${this.apiEndpoints.STORE_REMOVE(id)}?acc=${id}`
        );
        useCommonStore().getAlMyStores();
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async getFriendTeamLevel(id, member_id) {
      try {
        this.friendTeamLevel = [];
        this.friendloadTeamLevel = false;

        let data = await this.$request.get(
          this.apiEndpoints.TEAM_LEVEL(id) +
            "?team_member_id=" +
            member_id +
            "&acc=" +
            id
        );
        if (data.data) {
          this.friendTeamLevel = data.data.items;
          this.friendloadTeamLevel = true;
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    async getFriendTeamData(params) {
      if (params.page == 1) {
        this.friendTeamList = [];
        this.loadFriendTeams = false;
      }
      try {
        let response = await this.$request.get(
          this.apiEndpoints.TEAM_WITH_LEVEL(
            params.page,
            params.per_page,
            params.id,
            params.team_member_id,
            params.level_no
          )
        );
        if (response.data) {
          Array.prototype.push.apply(this.friendTeamList, response.data.items);
          this.loadFriendTeams = true;
        }
        return response.data;
      } catch (error) {
        // console.log("FromAPI:-" + error);
        showError(error.message);
      }
    },
    resetNotificationData() {
      this.friendTeamList = [];
      this.loadFriendTeams = false;
    },

    async getEarningHistory(params) {
      if (params.page == 1) {
        this.earningHistory = [];
        this.loadEarningHistory = false;
      }
      try {
        let response = await this.$request.get(
          this.apiEndpoints.EARNING_HISTORY_STORE(
            params.store_id,
            params.page,
            params.per_page
          )
        );
        if (response.data) {
          Array.prototype.push.apply(this.earningHistory, response.data.items);
          this.loadEarningHistory = true;
        }
        return response.data;
      } catch (error) {
        // console.log("FromAPI:-" + error);
        showError(error.message);
      }
    },
    resetEarningHistory() {
      this.earningHistory = [];
      this.loadEarningHistory = false;
    },

    async getFilterTagForFindStore() {
      try {
        this.tagList = [];
        this.loadTagList = false;
        let data = await this.$request.get(
          this.apiEndpoints.FIND_STORE_TAG_LIST + "?relatedTo=account"
        );
        if (data.data) {
          this.tagList = data.data.items;
          this.loadTagList = true;
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    async getDistanceFilterList() {
      try {
        this.distanceList = {};
        this.loadDistanceList = false;

        let data = await this.$request.get(
          this.apiEndpoints.FIND_STORE_DISTANCE_LIST
        );
        if (data.data) {
          this.distanceList = data.data.items;
          this.loadDistanceList = true;
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    async getCoupons(param) {
      try {
        this.couponsList = [];
        this.loadCoupons = false;

        let data = await this.$request.get(
          this.apiEndpoints.COUPONS_LIST(param)
        );
        if (data.data) {
          this.couponsList = data.data.items ? data.data.items : [];
          this.loadCoupons = true;
        }
      } catch (error) {
        // console.log("FromAPI:-" + error);
      }
    },
    async getStoreReview(id) {
      try {
        this.reviews = [];
        this.loadReviews = false;

        let data = await this.$request.get(
          `${this.apiEndpoints.GET_STORE_REVIEW(id)}?acc=${id}`
        );
        if (data.data && data.data.status) {
          this.reviews = data.data.items ? data.data.items : [];
          this.loadReviews = true;
        }
      } catch (error) {}
    },
    async getStoreBalance(storeId) {
      try {
        const data = await this.$request.post(
          this.apiEndpoints.STORE_BALANCE(storeId)
        );
        return data.data;
      } catch (error) {}
    },
    async getReviewDetail(storeId, reviewId) {
      try {
        this.reviewDetail = [];
        this.loadReviewDetail = false;

        let data = await this.$request.get(
          this.apiEndpoints.GET_STORE_REVIEW_DETAIL(storeId, reviewId)
        );
        if (data.data && data.data.status) {
          this.reviewDetail = data.data ? data.data : [];
          this.loadReviewDetail = true;
        }
      } catch (error) {}
    },
    async addStoreReviewReply(params, storeId, reviewId) {
      try {
        params.acc = storeId;
        let data = await this.$request.post(
          this.apiEndpoints.STORE_REVIEW_REPLY(storeId, reviewId),
          params
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {}
    },
    async downloadImage(url) {
      try {
        var base64 = await this.$request
          .get(url, {
            responseType: "arraybuffer",
          })
          .then((response) =>
            Buffer.from(response.data, "binary").toString("base64")
          );
        if (base64) {
          return "data:image/jpg;base64, " + base64;
        } else {
          return "";
        }
      } catch (error) {
        return "";
      }
    },
    async downloadPDF(url) {
      try {
        var base64 = await this.$request
          .get(url, {
            responseType: "arraybuffer",
          })
          .then((response) =>
            Buffer.from(response.data, "binary").toString("base64")
          );
        if (base64) {
          return "data:application/pdf;base64," + base64;
        } else {
          return "";
        }
      } catch (error) {
        return "";
      }
    },
    async getLoyaltyOffers(storeId) {
      if (!isValidStoreId(storeId)) return;
      try {
        this.loyaltyOffers = {};
        let data = await this.$request.get(
          `${this.apiEndpoints.STORE_OFFER_LOYALTY_OFFERS(storeId)}?multi=1`
        );
        if (data.status) {
          this.loyaltyOffers = data?.data?.data;
          return data;
        }
      } catch (error) {
        showError(error.message);
      }
    },
    async getRJRDeals(storeId) {
      if (!isValidStoreId(storeId)) return;
      try {
        this.rjrDeals = {};
        let data = await this.$request.get(
          this.apiEndpoints.RJR_DEALS(storeId)
        );
        if (data.status) {
          this.rjrDeals = data?.data;
          return data;
        }
      } catch (error) {
        showError(error.message);
      }
    },
    async setHomeStore(storeId, locationId) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.SETHOMESTORE(storeId, locationId)
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async addUserToSupportForEAIVVerified() {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.EAIV_SUPPORT_STATUS,
          { status: true }
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async getAltriaEaivStatus(params) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.ALTRIA_EAIV_STATUS,
          params
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async resubmitAltriaStatus(storeID) {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.RESUBMIT_ALTRIA_STATUS,
          { store_id: storeID }
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async addUserToSupportForEAIVVerified() {
      try {
        let data = await this.$request.post(
          this.apiEndpoints.EAIV_SUPPORT_STATUS,
          { status: true }
        );
        if (data.data && data.data.status) {
          return data.data;
        } else {
          return data.data;
        }
      } catch (error) {
        return { status: false, message: error.message };
      }
    },
    async getDealsGroups(storeid) {
      try {
        let param = storeid
          ? `?filter_store=${storeid}&enforce_age_restrictions=1`
          : `?enforce_age_restrictions=1`;
        let data = await this.$request.get(
          this.apiEndpoints.CAMPAIGNS_DEAL_GROUPS + param
        );
        if (data.data) {
          this.dealsGroup = data.data.items ? data.data.items : [];
        }
        return data.data;
      } catch (error) {
        // console.log("APIError:-", error);
        throw error;
      }
    },
    async getDiscountDeals(params) {
      try {
        if (params?.store_id) {
          params.acc = params.store_id;
        }
        // latitude/longitude are added only when the customer has already
        // allowed location access (web, PWA and Cordova) — see utils/geolocation
        const query = await withAllowedCoordinates(params);
        const response = await this.$request.get(
          `${this.apiEndpoints.DISCOUNTS_DEALS}?${new URLSearchParams(
            query
          ).toString()}`
        );
        return response.data;
      } catch (error) {
        console.error("get Discount Deals error: ", error);
        throw error;
      }
    },

    async getOnlineDiscountDetails({ id, store_id }) {
      try {
        const response = await this.$request.get(
          this.apiEndpoints.ONLINE_DISCOUNT_DETAILS(store_id, id)
        );
        return response?.data?.item;
      } catch (error) {
        console.error("get Online Discount Details error: ", error);
        throw error;
      }
    },

    // Paged store list for an online discount. Resolves to null if the axios
    // interceptor cancels the request (callers treat that as superseded).
    async getOnlineDiscountLocations({ store_id, id, page, per_page, search }) {
      try {
        const response = await this.$request.get(
          this.apiEndpoints.ONLINE_DISCOUNT_LOCATIONS(store_id, id),
          { params: { page, per_page, search } }
        );
        return response ? response.data : null;
      } catch (error) {
        console.error("get Online Discount Locations error: ", error);
        throw error;
      }
    },

    async getEarnAsYouShop(storeId, param = {}) {
      if (!isValidStoreId(storeId)) return;
      try {
        param.acc = storeId;
        let response = await this.$request.get(
          this.apiEndpoints.STORE_DEALS(storeId, param)
        );

        return response.data;
      } catch (error) {
        console.error("get Discount Deals error: ", error);
        throw error;
      }
    },

    async getOnlineDiscountsCategory(params) {
      try {
        if (params?.store_id) {
          params.acc = params.store_id;
        }
        const query = await withAllowedCoordinates(params);
        let response = await this.$request.get(
          `${this.apiEndpoints.ONLINE_DISCOUNTS_CATEGORY}?${new URLSearchParams(
            query
          ).toString()}`
        );

        return response.data;
      } catch (error) {
        console.error("get Online Discounts Category error: ", error);
        throw error;
      }
    },

    async setDiscountViewed(discountItems, account_id) {
      const userInfo = useUserInfo();
      const tmpIds = [];
      for (const item of discountItems) {
        tmpIds.push(item.id);
      }
      let params = {
        discount_id: tmpIds.join(","),
        customer_id: userInfo.user_info.id,
        type: "view",
        account_id: account_id,
      };

      try {
        let response = await this.$request.post(
          `${this.apiEndpoints.DISCOUNTS_DEALS}/activity`,
          params
        );
        return response.data;
      } catch (error) {
        console.error("set Discount Viewed error: ", error);
        throw error;
      }
    },

    async setDiscountClicked(discount_id, account_id) {
      const userInfo = useUserInfo();
      let params = {
        discount_id: discount_id,
        customer_id: userInfo.user_info.id,
        type: "click",
        account_id: account_id,
      };

      try {
        let response = await this.$request.post(
          `${this.apiEndpoints.DISCOUNTS_DEALS}/activity`,
          params
        );
        return response.data;
      } catch (error) {
        console.error("set Discount Clicked error: ", error);
        throw error;
      }
    },

    async addToCardDiscount(params) {
      try {
        let response = await this.$request.post(
          `${this.apiEndpoints.ADD_TO_CARD_DISCOUNT}`,
          params
        );
        return response.data;
      } catch (error) {
        console.error("set Discount Clicked error: ", error);
        throw error;
      }
    },
  },
});
